import { useEffect, useState, type ReactNode } from 'react';
import type { AccountInfo } from '@azure/msal-browser';
import { createBusiness, findOwnBusiness, findSharedBusinesses, openShareLink, whoAmI, type FoundBusiness, type Graph, type Me } from '@nandanam/sync';
import { Icon, Logo } from '../ui/icons';
import { signIn, signOutHere, setMode } from '../onedrive/auth';
import { errorText } from '../app/store';

/** Screens shown before the app opens: Microsoft sign-in, then choosing the business folder. */
function Frame({ children }: { children: ReactNode }) {
  return (
    <div className="start-wrap">
      <div className="start-card">
        <div className="start-brand"><Logo size={48} light={false} /><div><b>NANDANAM</b><span>FARMS</span></div></div>
        {children}
      </div>
    </div>
  );
}

const tryDemo = () => { setMode('demo'); location.reload(); };

export function SignIn({ error }: { error?: string }) {
  const [busy, setBusy] = useState(false);
  return (
    <Frame>
      <h1 className="start-h">Sign in to open the business</h1>
      <p className="start-p">Use the Microsoft account the business folder is shared with (Outlook, Hotmail or Live). The bookings are kept in that OneDrive folder, not in this app.</p>
      {error && <div className="warn-box" role="alert">{error}</div>}
      <button className="btn primary big block" disabled={busy} onClick={() => { setBusy(true); signIn().catch(e => { setBusy(false); alert(errorText(e)); }); }}>
        <Icon name="user" size={17} />{busy ? 'Opening Microsoft sign-in…' : 'Sign in with Microsoft'}
      </button>
      <button className="btn block" onClick={tryDemo}>Try the demo with sample bookings</button>
      <p className="start-fine">The demo keeps sample data in this browser only and never touches OneDrive.</p>
    </Frame>
  );
}

export function ChooseFolder({ graph, account, onReady }: { graph: Graph; account: AccountInfo; onReady: (me: Me, biz: FoundBusiness) => void }) {
  const [me, setMe] = useState<Me | null>(null);
  const [own, setOwn] = useState<FoundBusiness | null>(null);
  const [shared, setShared] = useState<FoundBusiness[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [link, setLink] = useState('');
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let live = true;
    setError(null);
    (async () => {
      try {
        const m = await whoAmI(graph);
        if (!live) return;
        setMe(m);
        const [o, s] = await Promise.all([findOwnBusiness(graph, m), findSharedBusinesses(graph).catch(() => [] as FoundBusiness[])]);
        if (!live) return;
        setOwn(o); setShared(s);
      } catch (e) { if (live) setError(errorText(e)); }
    })();
    return () => { live = false; };
  }, [attempt]);

  const run = async (label: string, f: () => Promise<FoundBusiness>) => {
    setBusy(label); setError(null);
    try { const b = await f(); onReady(me!, b); } catch (e) { setError(errorText(e)); setBusy(null); }
  };
  const loading = !error && (!me || shared === null);
  const choices = [...(own ? [own] : []), ...(shared ?? [])];

  return (
    <Frame>
      <h1 className="start-h">Choose the business folder</h1>
      <p className="start-p">Signed in as <b>{account.name || account.username}</b>{account.username && account.name ? <> ({account.username})</> : null}.</p>
      {error && <div className="warn-box" role="alert">{error} <button className="btn small" onClick={() => setAttempt(a => a + 1)}>Try again</button></div>}
      {loading && <div className="muted">Looking in your OneDrive…</div>}
      {!loading && me && <>
        {choices.length > 0 && <div className="start-list">
          {choices.map(b => (
            <button key={b.driveId + b.folderId} className="start-choice" disabled={!!busy} onClick={() => run(b.folderId, async () => b)}>
              <Icon name="folder" size={20} />
              <span><b>{b.name}</b><span className="muted">{b === own ? 'In your OneDrive' : `Shared with you by ${b.sharedBy}`}</span></span>
              <Icon name="right" size={16} />
            </button>
          ))}
        </div>}
        {!choices.length && <div className="info-box">No business folder found for this account yet.</div>}

        <div className="start-or">Someone shared the folder with you?</div>
        <div className="start-row">
          <input className="input" placeholder="Paste the OneDrive link they sent" value={link} onChange={e => setLink(e.target.value)} />
          <button className="btn" disabled={!link.trim() || !!busy} onClick={() => run('link', () => openShareLink(graph, link))}>{busy === 'link' ? 'Opening…' : 'Open'}</button>
        </div>

        {!own && <>
          <div className="start-or">Starting fresh?</div>
          <button className="btn primary block" disabled={!!busy} onClick={() => run('new', () => createBusiness(graph, me))}>
            <Icon name="plus" size={16} />{busy === 'new' ? 'Creating the folder…' : 'Start the business in my OneDrive'}
          </button>
          <p className="start-fine">Creates a folder called “Nandanam Farms” in your OneDrive. You then share it with the people who should use the app.</p>
        </>}
      </>}
      <div className="start-foot">
        <button className="btn ghost small" onClick={() => { void signOutHere().then(() => location.reload()); }}>Use a different Microsoft account</button>
        <button className="btn ghost small" onClick={tryDemo}>Try the demo</button>
      </div>
    </Frame>
  );
}

export function Loading({ text = 'Opening…' }: { text?: string }) {
  return <Frame><div className="muted" style={{ textAlign: 'center', padding: '12px 0' }}>{text}</div></Frame>;
}
